import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Loader2 } from 'lucide-react';
import CompanyNameModal from '../components/CompanyNameModal';

export default function AuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const [error, setError] = useState(null);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [userName, setUserName] = useState('');
  const [processing, setProcessing] = useState(true);
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const handleCallback = async () => {
      const token = searchParams.get('token');
      const isNew = searchParams.get('new') === 'true';
      const errorParam = searchParams.get('error');

      if (errorParam) {
        setError(getErrorMessage(errorParam));
        setProcessing(false);
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      if (token) {
        try {
          const user = await loginWithToken(token);

          if (isNew && user.needs_company_name) {
            setUserName(user.name || '');
            setShowCompanyModal(true);
            setProcessing(false);
          } else {
            navigate('/dashboard', { replace: true });
          }
        } catch (err) {
          console.error('AuthCallback - Error:', err);
          navigate('/dashboard', { replace: true });
        }
      } else {
        setError('Authentication failed. Please try again.');
        setProcessing(false);
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    handleCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getErrorMessage = (code) => {
    const messages = {
      'google_auth_failed': 'Google authentication failed. Please try again.',
      'no_email': 'Could not retrieve email from Google. Please try again.',
      'default': 'An error occurred. Please try again.'
    };
    return messages[code] || messages['default'];
  };

  const handleCompanyComplete = () => {
    navigate('/dashboard', { replace: true });
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="max-w-md rounded-sm">
          <CardContent className="p-8 text-center">
            <p className="text-red-600 font-medium mb-2">Authentication Error</p>
            <p className="text-muted-foreground">{error}</p>
            <p className="text-sm text-muted-foreground mt-4">Redirecting to login...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (showCompanyModal) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <CompanyNameModal 
          isOpen={true} 
          onComplete={handleCompanyComplete}
          userName={userName}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Card className="max-w-md rounded-sm">
        <CardContent className="p-8 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-orange-500" />
          <p className="font-medium">Signing you in...</p>
          <p className="text-sm text-muted-foreground mt-2">Please wait while we set up your account.</p>
        </CardContent>
      </Card>
    </div>
  );
}
